import {
  Text,
  GreyGrey40,
  GreyGrey60,
  GreyGrey70,
  GreyWhite,
  IndigoIndigo60,
  Link,
  Primary13Regular,
  Primary13SemiBold,
  ShadowXs,
  Box,
  Flex
} from '@sequencehq/core-components'
import { required } from '@sequencehq/utils/validation'
import {
  Checkbox,
  TextInputField,
  SimpleModal
} from '@sequencehq/core-components'
import { useForm } from '@sequencehq/utils'
import { useTeamSettings } from '../useTeamSettings'
import { Member } from '@stytch/react/b2b'
import { SYSTEM_ROLES } from 'lib/rbac/systemRoles'
import {
  ROLE_DOCS_URL,
  ROLE_OPTIONS,
  ROLE_SELECTION_HELP_TEXT
} from './utils/roleOptions'
import { useRoleManagementCapabilities } from './utils/useRoleManagementCapabilities'

interface Props {
  member: Member
  trigger: React.ReactElement
}

export const EditMemberModal = ({ member, trigger }: Props) => {
  const { updateMemberRoles } = useTeamSettings()
  const { isAdmin, getSubmissionRoles, canAssignRoles } =
    useRoleManagementCapabilities()

  const memberRoles = member.roles
    .map(role => role.role_id)
    .filter(role => Object.values(SYSTEM_ROLES).includes(role as any))

  const userId = member.trusted_metadata.sequenceUserId as string

  const { fields, queries } = useForm({
    value: {
      roles: memberRoles
    },
    fieldConfiguration: [
      {
        property: 'roles',
        validation: canAssignRoles ? [required] : []
      }
    ]
  })

  if (!isAdmin) {
    return null
  }

  return (
    <SimpleModal
      trigger={trigger}
      minHeight="0px"
      data-testid="team.edit.modal"
      title="Edit user"
      submitButtonText="Update"
      submittingButtonText="Updating..."
      onSubmit={async () => {
        if (!queries.isValid) return false

        await updateMemberRoles({
          id: userId,
          roles: getSubmissionRoles(queries.formData.roles)
        })

        return true
      }}
    >
      <Flex
        id="edit-member-form"
        data-testid="team.edit.form"
        flexDirection="column"
        gap="16px"
      >
        <TextInputField
          label="Email"
          isDisabled
          id="memberEmail"
          data-testid="team.edit.email"
          value={member.email_address}
        />

        <Flex flexDirection="column" gap="12px">
          <Flex flexDirection="column" gap="4px">
            <Box {...Primary13SemiBold} color={GreyGrey70}>
              Role
            </Box>
            <Text {...Primary13Regular} color={GreyGrey70}>
              {ROLE_SELECTION_HELP_TEXT}{' '}
              <Link.Unstyled
                style={{ textDecoration: 'none', color: IndigoIndigo60 }}
                to={ROLE_DOCS_URL}
                target="_blank"
              >
                learn more.
              </Link.Unstyled>
            </Text>
          </Flex>

          <Flex
            data-testid="team.edit.roleList"
            borderRadius="8px"
            border={`1px solid ${GreyGrey40}`}
            boxShadow={ShadowXs}
            backgroundColor={GreyWhite}
            flexDirection="column"
          >
            {ROLE_OPTIONS.map((role, index) => (
              <Flex
                padding="16px"
                key={role.value}
                borderBottom={
                  index === ROLE_OPTIONS.length - 1
                    ? 'none'
                    : `1px solid ${GreyGrey40}`
                }
              >
                <Checkbox
                  data-testid={`team.edit.role.${role.value}`}
                  disabled={!canAssignRoles}
                  isChecked={fields.roles.value.includes(role.value)}
                  onChange={e => {
                    const currentRoles = fields.roles.value
                    const newRoles = e.target.checked
                      ? [...currentRoles, role.value]
                      : currentRoles.filter(r => r !== role.value)
                    fields.roles.onChange(newRoles)
                  }}
                  label={
                    <Flex flexDirection="column" gap="4px">
                      <Text {...Primary13SemiBold}>{role.label}</Text>
                      <Text {...Primary13Regular} color={GreyGrey60}>
                        {role.description}
                      </Text>
                    </Flex>
                  }
                />
              </Flex>
            ))}
          </Flex>

          {fields.roles.validationErrors &&
            fields.roles.validationErrors.length > 0 && (
              <Text
                {...Primary13Regular}
                data-testid="team.edit.rolesError"
                color="red.500"
              >
                {String(fields.roles.validationErrors[0])}
              </Text>
            )}
        </Flex>
      </Flex>
    </SimpleModal>
  )
}
